import { UserRoles } from "@prismancy/shared";
import { PanelLeft, PanelRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import PrismancyLogo from "./PrismancyLogo";
import { useWorkspaceTimezone } from "../hooks/common/useWorkspaceTimezone";
import useUserProfile from "../hooks/profile/useUserProfile";
import useWorkspace from "../hooks/workspace/useWorkspace";
import IconFont from "../elements/IconFont";
import DrawerNav from "./DrawerNav";
import ViewInvites from "./ViewInvites";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import { appRoutes } from "../../routes/appRoutes";
import ReactTooltip from "./ReactTooltip";
import WebExtensionSection from "./WebExtensionSection";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isDesktopCollapsed: boolean;
  onToggleDesktopCollapsed: () => void;
}

const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  isDesktopCollapsed,
  onToggleDesktopCollapsed,
}: DrawerProps): React.ReactElement => {
  const { profile, isLoading: isProfileLoading } = useUserProfile();
  const { workspace } = useWorkspace();
  const [buildNumber, setBuildNumber] = useState("");
  const [workspaceDatetime, setWorkspaceDatetime] = useState("");
  const { formatted } = useWorkspaceTimezone({
    timezone: workspace?.timezone,
  });

  const location = useLocation();
  const navigate = useNavigate();

  const handleOnChangeWorkspace = () => {
    onClose();
  };

  const handleOnOpenViewInvites = () => {
    onClose();
  };

  useEffect(() => {
    if (workspace?.timezone && formatted) {
      setWorkspaceDatetime(formatted);
    }
  }, [workspace, formatted]);

  useEffect(() => {
    onClose();
  }, [location]);

  useEffect(() => {
    const getBuildNumber = async () => {
      // Check if Chrome extension APIs are available
      const isChromeExtension =
        typeof chrome !== "undefined" && chrome?.runtime?.getManifest;

      if (!isChromeExtension) {
        // Running in regular web page (Safari, Firefox, etc.)
        console.log(
          "Chrome extension APIs not available - running in web page",
        );
        const envBuildNumber = import.meta.env.VITE_BUILD_NUMBER;
        if (envBuildNumber) {
          setBuildNumber(
            envBuildNumber.startsWith("v")
              ? envBuildNumber
              : `v${envBuildNumber}`,
          );
        }
        return; // Return undefined (no cleanup needed)
      }

      try {
        setBuildNumber(`v${chrome.runtime.getManifest().version}`);
      } catch (error) {
        console.error("Error getting manifest version:", error);
      }
    };

    getBuildNumber();
  }, []);

  return (
    <>
      {isOpen && (
        <button
          onClick={onClose}
          type="button"
          className="ui-layer-drawer-backdrop fixed inset-0 h-full w-full bg-[var(--ui-drawer-overlay)] leading-[1] [clip:inherit] [clip-path:inherit]"
          aria-label="Close menu"
        />
      )}
      <div
        data-desktop-collapsed={isDesktopCollapsed}
        className={`ui-layer-drawer-panel fixed left-0 top-0 flex h-full w-[var(--prismancy-drawer-width)] translate-x-[var(--prismancy-drawer-offset)] cursor-default flex-col border-r border-r-border-default bg-background-surface-01 [clip:inherit] [clip-path:inherit] transition-[transform,width,padding] duration-200 [transition-timing-function:var(--prismancy-transition-timing-function)] ${isOpen ? "[--prismancy-page-scrollbar-width:15px] [--prismancy-drawer-offset:0] [--prismancy-drawer-width:312px]" : ""}`}
      >
        <div className="ui-drawer-header flex items-center gap-[16px] py-[16px]">
          <a
            href={appRoutes.experiments.root}
            className="ui-drawer-brand leading-[1]"
            onClick={(event) => {
              event.preventDefault();
              navigate(appRoutes.experiments.root);
            }}
          >
            {isDesktopCollapsed && !isOpen ? (
              <PrismancyLogo variant="icon" className="ui-drawer-brand-icon" />
            ) : (
              <PrismancyLogo variant="full" className="ui-drawer-brand-full" />
            )}
          </a>
          <button
            id="desktop-sidebar-toggle"
            type="button"
            onClick={onToggleDesktopCollapsed}
            className="ui-drawer-desktop-toggle btn-icon h-8 w-8 items-center justify-center p-0 text-heading"
            aria-label={isDesktopCollapsed ? "Open Sidebar" : "Close Sidebar"}
          >
            {isDesktopCollapsed ? (
              <PanelRight size={20} />
            ) : (
              <PanelLeft size={20} />
            )}
          </button>
          <ReactTooltip anchorSelect="#desktop-sidebar-toggle" place="right">
            {isDesktopCollapsed ? "Open Sidebar" : "Close Sidebar"}
          </ReactTooltip>
          <button
            type="button"
            onClick={onClose}
            className="ui-drawer-close leading-[1]"
            aria-label="Close menu"
          >
            <IconFont name="x" size={24} />
          </button>
        </div>
        <WorkspaceSwitcher
          isDesktopCollapsed={isDesktopCollapsed}
          onChange={() => handleOnChangeWorkspace()}
        />
        <DrawerNav isDesktopCollapsed={isDesktopCollapsed} />
        <WebExtensionSection isDesktopCollapsed={isDesktopCollapsed} />
        <div className="ui-drawer-footer absolute bottom-0 left-0 flex w-full flex-col gap-[8px]">
          <ViewInvites
            className="ui-drawer-invites"
            onOpen={handleOnOpenViewInvites}
          />
          <NavLink
            id="drawer-profile-link"
            to={appRoutes.profile}
            title={isDesktopCollapsed ? undefined : "Profile"}
            aria-label="Profile"
            className="ui-drawer-profile flex w-full"
          >
            <div className="ui-drawer-profile-icon flex shrink-0 items-center">
              <div className="bg-background-surface-03 flex h-[32px] gap-[8px] items-center justify-center p-[8px] rounded-[500px] w-[32px]">
                <IconFont name="user" className="text-[20px]" />
              </div>
            </div>
            <div className="js-ui-navigation-drawer-profile-details flex flex-1 flex-col items-start gap-[4px]">
              <span className="block text-heading text-md font-normal leading-6 tracking-normal truncate max-w-[180px]">
                {isProfileLoading ? "Loading..." : profile?.user.name}
              </span>
              <span className="block text-body-muted text-xs truncate max-w-[180px]">
                {isProfileLoading ? "Loading..." : profile?.user.email}
              </span>
              <span className="text-accent font-medium text-xs leading-[18px] tracking-normal capitalize">
                {workspace?.role_name ? workspace.role_name : UserRoles.member}
              </span>
            </div>
            <div className="flex items-center h-[24px] shrink-0">
              <IconFont name="chevron-right" size={24} />
            </div>
          </NavLink>
          {isDesktopCollapsed && (
            <ReactTooltip anchorSelect="#drawer-profile-link" place="right">
              Profile
            </ReactTooltip>
          )}
        </div>
        {buildNumber && (
          <span className="ui-drawer-build-number text-body-muted absolute right-0 bottom-0 text-xs p-3">
            {buildNumber}
          </span>
        )}
        {workspaceDatetime && (
          <span className="ui-drawer-workspace-time text-body-muted absolute left-0 bottom-0 text-xs p-3">
            {workspaceDatetime}
          </span>
        )}
      </div>
    </>
  );
};

export default Drawer;
